/**
 * API client for fetching service status data from the Cloud Function
 * that retrieves status information by checking service health endpoints
 * and Cloud Run API.
 */

const SERVICE_STATUS_API_URL =
  process.env.REACT_APP_SERVICE_STATUS_API_URL ||
  'https://us-east4-sabal-some.cloudfunctions.net/getServiceStatuses';

/**
 * Fetches the current status of all services
 * 
 * @returns {Promise<Object>} Promise that resolves to the service status data
 */
export const fetchServiceStatuses = async () => {
  try {
    const response = await fetch(SERVICE_STATUS_API_URL, { signal: AbortSignal.timeout(15000) });
    if (!response.ok) {
      throw new Error(`Status API returned ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching service statuses:', error);
    
    // Return a minimal error structure that the UI can handle
    return {
      services: {
        'sabal-cloud': { status: 'unknown' },
        'sabalsql-dbam': { status: 'unknown' },
        'debezium-server': { status: 'unknown' },
        'debezium-webhook': { status: 'unknown' },
        'someac': { status: 'ok' } // UI is accessible so someac is running
      },
      timestamp: new Date().toISOString(),
      error: error.message
    };
  }
};

/**
 * Fetches detailed status information for a specific service
 * 
 * @param {string} serviceName - Name of the service to get detailed status for
 * @returns {Promise<Object>} Promise that resolves to the detailed service status
 */
export const fetchServiceDetail = async (serviceName) => {
  try {
    const response = await fetch(
      `${SERVICE_STATUS_API_URL}?service=${serviceName}`,
      { signal: AbortSignal.timeout(15000) }
    );
    if (!response.ok) {
      throw new Error(`Status API returned ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching detailed status for ${serviceName}:`, error);
    
    // Return a minimal error structure that the UI can handle
    return {
      service: serviceName,
      status: 'unknown',
      timestamp: new Date().toISOString(),
      error: error.message
    };
  }
};
